import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, Phone, Truck } from "lucide-react";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: "Services", href: "/services", hasDropdown: true },
    { name: "Areas We Cover", href: "/areas-we-cover" },
    { name: "About Us", href: "/about" },
    { name: "Reviews", href: "/reviews" },
    { name: "Contact", href: "/contact" },
  ];

  const serviceLinks = [
    { name: "House Removals", href: "/services/house-removals" },
    { name: "Office Removals", href: "/services/office-removals" },
    { name: "Man and Van", href: "/services/man-and-van" },
    { name: "Storage", href: "/services/storage" },
    { name: "Specialist Moves", href: "/services/specialist-moves" },
  ];

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + "/");

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header">
      <div className="container-max">
        <div className="flex items-center justify-between h-20 px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <div className="w-10 h-10 bg-[#0F172A] flex items-center justify-center">
              <Truck className="w-6 h-6 text-[#FF5A1F]" />
            </div>
            <div>
              <span className="font-bold text-xl text-[#0F172A] font-['Outfit'] tracking-tight">HITECH</span>
              <span className="font-bold text-xl text-[#FF5A1F] font-['Outfit'] tracking-tight">TRANSIT</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <Link
                      to={item.href}
                      className={`flex items-center gap-1 text-sm font-semibold transition-colors ${
                        isActive(item.href) ? "text-[#FF5A1F]" : "text-[#0F172A] hover:text-[#FF5A1F]"
                      }`}
                      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {item.name}
                      <ChevronDown className="w-4 h-4" />
                    </Link>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 pt-2">
                        <div className="bg-white border border-slate-200 shadow-lg py-2 min-w-[200px]">
                          {serviceLinks.map((link) => (
                            <Link
                              key={link.name}
                              to={link.href}
                              className="block px-4 py-2 text-sm text-[#0F172A] hover:bg-slate-50 hover:text-[#FF5A1F]"
                              data-testid={`nav-dropdown-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {link.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-sm font-semibold transition-colors ${
                      isActive(item.href) ? "text-[#FF5A1F]" : "text-[#0F172A] hover:text-[#FF5A1F]"
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:07346209593" className="flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:text-[#FF5A1F] transition-colors">
              <Phone className="w-4 h-4" />
              07346 209593
            </a>
            <Link to="/instant-quote" className="btn-primary" data-testid="header-get-quote-btn">
              Free Quote in 60s
            </Link>
          </div>

          {/* Mobile: Click-to-call + Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href="tel:07346209593"
              className="w-10 h-10 bg-[#FF5A1F] text-white flex items-center justify-center"
              aria-label="Call us"
              data-testid="mobile-call-btn"
            >
              <Phone className="w-5 h-5" />
            </a>
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white">
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`block text-base font-semibold ${
                      isActive(item.href) ? "text-[#FF5A1F]" : "text-[#0F172A]"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.name}
                  </Link>
                  {item.hasDropdown && (
                    <div className="mt-2 ml-4 space-y-2">
                      {serviceLinks.map((link) => (
                        <Link
                          key={link.name}
                          to={link.href}
                          className="block text-sm text-slate-600 hover:text-[#FF5A1F]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t border-slate-200">
                <a href="tel:07346209593" className="flex items-center gap-2 text-sm font-semibold text-[#0F172A] mb-4">
                  <Phone className="w-4 h-4" />
                  07346 209593
                </a>
                <Link
                  to="/instant-quote"
                  className="btn-primary w-full text-center block"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-get-quote-btn"
                >
                  Get Instant Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
